/**
 * جدول با ویرایش دسته‌جمعی (نسخهٔ ارتقایافته)
 * - دکمهٔ «ویرایش دسته‌جمعی» جدول را به حالت edit می‌برد
 * - editType هر ستون: text | number | select | boolean | date | textarea
 * - اعتبارسنجی سلولی با col.editRule(value, row) → پیامِ خطا یا null
 * - «اعمال روی انتخاب‌شده‌ها»: Editor متناسب + خلاصهٔ تأیید
 * - ذخیرهٔ تراکنشی با onSaveMany(changes) — یا fallback ردیف‌به‌ردیف با onSave
 * - محدودکردنِ ویرایش هر ردیف با canEditRow(row) → true | false | "دلیل"
 * - editDependent: { clear: ["field"], onChange?: (val, row, changeFn) => void }
 * - انتخابِ کنترل‌شده (اختیاری): `selectedRowKeys` + `onSelectionChange` ⇒ تیکِ ردیف‌ها همیشه
 *   (نه فقط در حالتِ ویرایش) فعال است تا صفحه کارهای دسته‌جمعیِ خودش (چاپ/لیبل/خروجی) را بسازد.
 */
import React, { useState, useMemo, useEffect, useCallback, startTransition } from "react";
import { Input, InputNumber, Select, Switch, Button, Popconfirm, Space, message, Tag, Tooltip, Modal } from "antd";
import Alert from "./GxAlert";
import {
  EditOutlined, SaveOutlined, CloseOutlined, DeleteOutlined, ThunderboltOutlined,
} from "@ant-design/icons";
import GxTable from "./GxTable";
import gridKeyNav from "../utils/gridKeyNav";
import ErrorBoundary from "./ErrorBoundary";
import { JalaliDateInput } from "../utils/dateUtils";

/**
 * Selectی که مقدارِ تازه هم می‌پذیرد — تایپ کن و Enter بزن.
 *
 * چرا لازم است: ستون‌هایی مثل «رنگ» فهرستِ بسته ندارند؛ کاربر در ویرایشِ
 * دسته‌جمعی فقط رنگ‌های قبلی را می‌دید و رنگِ تازه را نمی‌توانست وارد کند.
 * `mode="tags"` ورودیِ آزاد می‌دهد؛ اینجا به یک مقدارِ تکی محدود می‌شود.
 */
export function CreatableSelect({ value, options, onChange, onCreate, placeholder, ...rest }) {
  return (
    <Select
      {...rest}
      mode="tags"
      maxTagCount={1}
      value={value ? [value] : []}
      options={options}
      placeholder={placeholder || "انتخاب یا تایپِ مقدار تازه"}
      optionFilterProp="label"
      onChange={(arr) => {
        // فقط آخرین انتخاب می‌ماند تا رفتار «تک‌مقداری» حفظ شود
        const v = Array.isArray(arr) && arr.length ? String(arr[arr.length - 1]).trim() : null;
        // مقدارِ تازه (خارج از options) همین‌جا به کاتالوگِ سراسری هم اضافه می‌شود
        // تا در دراپ‌داونِ بعدی دوباره تعریف نشود.
        if (v && onCreate && !(options || []).some((o) => String(o.value) === v)) onCreate(v);
        onChange(v || null);
      }}
    />
  );
}

function resolveOptions(col, row) {
  if (!col) return [];
  const safeRow = row || {};
  try {
    if (typeof col.options === "function") return col.options(safeRow) || [];
  } catch {
    return [];
  }
  return col.options || [];
}

/** اگر مقدار فعلی در لیست نباشد (legacy / رنگ آزاد)، گزینهٔ موقت اضافه می‌شود تا Select کرش نکند */
function optionsWithCurrent(opts, current, labelFn) {
  const list = Array.isArray(opts) ? [...opts] : [];
  if (current == null || current === "") return list;
  const hit = list.some((o) => String(o.value) === String(current));
  if (!hit) {
    const lab = typeof labelFn === "function" ? labelFn(current) : String(current);
    list.push({ value: current, label: lab });
  }
  return list;
}

function optionLabel(col, value, row) {
  const opts = resolveOptions(col, row);
  const hit = opts.find((o) => o.value === value);
  return hit ? hit.label : (value == null || value === "" ? "—" : String(value));
}

/** کلیدِ ردیف عددی است یا رشته‌ای؟ کلیدهای رشته‌ای (مثلِ کدِ تولید) نباید
 *  با Number() به NaN تبدیل شوند. */
const rowId = (id) => (/^\d+$/.test(String(id)) ? Number(id) : String(id));

export default function EditableTable({
  dataSource,
  columns,
  onSave,
  onSaveMany,
  onDelete,
  onDeleteMany,
  canEditRow,
  rowKey = "id",
  loading,
  pagination,
  title,
  layoutId,
  scroll,
  extraToolbar,
  gxSummary,
  /* پیش‌فرضِ ستون‌های بسته و نسخهٔ چیدمان — قبلاً فهرستِ propها اینجا تمام
     می‌شد و هرچه بیشتر پاس می‌دادی بی‌صدا دور ریخته می‌شد. */
  gxDefaultHidden,
  gxLayoutVersion,
  selectedRowKeys,
  onSelectionChange,
  rowClassName: rowClassNameProp,
}) {
  const [editMode, setEditMode] = useState(false);
  const [changes, setChanges] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [ownSelectedKeys, setOwnSelectedKeys] = useState([]);
  const selectionControlled = Array.isArray(selectedRowKeys);
  const selectedKeys = selectionControlled ? selectedRowKeys : ownSelectedKeys;
  const setSelectedKeys = selectionControlled ? (onSelectionChange || (() => {})) : setOwnSelectedKeys;
  // انتخابِ کنترل‌شده بعد از ذخیره/انصراف می‌ماند (مثلاً برای چاپِ لیبلِ همان ردیف‌ها)
  const resetOwnSelection = () => { if (!selectionControlled) setOwnSelectedKeys([]); };
  const [applyModal, setApplyModal] = useState(false);
  const [applyField, setApplyField] = useState(null);
  const [applyValue, setApplyValue] = useState(null);
  const [applyConfirm, setApplyConfirm] = useState(false);

  const changedCount = Object.keys(changes).filter((k) => Object.keys(changes[k]).length > 0).length;

  useEffect(() => {
    if (!editMode || changedCount === 0) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [editMode, changedCount]);

  const rowEditState = useCallback((row) => {
    if (!canEditRow) return { ok: true };
    const r = canEditRow(row);
    if (r === true || r === undefined) return { ok: true };
    return { ok: false, reason: typeof r === "string" ? r : "این ردیف قابلِ ویرایش نیست" };
  }, [canEditRow]);

  const change = useCallback((rowId, field, val, col, row) => {
    setChanges((prev) => {
      const nextRow = { ...(prev[rowId] || {}), [field]: val };
      // وابستگی: پاک‌کردن فیلدهای وابسته
      if (col?.editDependent?.clear) {
        col.editDependent.clear.forEach((f) => { nextRow[f] = null; });
      }
      return { ...prev, [rowId]: nextRow };
    });
    if (col && typeof col.editRule === "function") {
      const err = col.editRule(val, row);
      setErrors((prev) => {
        const rowErr = { ...(prev[rowId] || {}) };
        if (err) rowErr[field] = err; else delete rowErr[field];
        const next = { ...prev, [rowId]: rowErr };
        if (!Object.keys(rowErr).length) delete next[rowId];
        return next;
      });
    }
    if (typeof col?.editDependent?.onChange === "function") {
      col.editDependent.onChange(val, row, (f, v) => {
        setChanges((prev) => ({
          ...prev,
          [rowId]: { ...(prev[rowId] || {}), [f]: v },
        }));
      });
    }
  }, []);

  const effectiveChanges = useMemo(() => {
    const byId = new Map((dataSource || []).map((r) => [String(r[rowKey]), r]));
    const out = {};
    Object.entries(changes).forEach(([id, fields]) => {
      const row = byId.get(String(id));
      const diff = {};
      Object.entries(fields).forEach(([f, v]) => {
        const orig = row ? row[f] : undefined;
        const same = (v ?? null) === (orig ?? null)
          || (typeof v === "number" && Number(orig) === v);
        if (!same) diff[f] = v;
      });
      if (Object.keys(diff).length) out[id] = diff;
    });
    return out;
  }, [changes, dataSource, rowKey]);

  const cellErrorCount = Object.values(errors)
    .reduce((s, e) => s + Object.keys(e).filter((k) => k !== "__row").length, 0);

  const handleSaveAll = async () => {
    const entries = Object.entries(effectiveChanges);
    if (!entries.length) { message.info("تغییری وجود ندارد"); return; }
    if (cellErrorCount > 0) { message.error("ابتدا خطاهای اعتبارسنجی را برطرف کنید"); return; }
    setSaving(true);
    try {
      if (onSaveMany) {
        try {
          await onSaveMany(entries.map(([id, fields]) => ({ id: rowId(id), ...fields })));
          message.success(`${entries.length} ردیف ذخیره شد`);
          setChanges({}); setErrors({}); setEditMode(false); resetOwnSelection();
        } catch (e) {
          const det = e?.response?.data?.detail;
          if (det && det.errors) {
            const next = {};
            det.errors.forEach((er) => { next[er.id] = { __row: er.detail }; });
            setErrors(next);
            message.error(det.message || "برخی ردیف‌ها خطا داشتند؛ هیچ تغییری ذخیره نشد");
          } else {
            message.error((typeof det === "string" && det) || "خطا در ذخیرهٔ گروهی");
          }
        }
      } else if (onSave) {
        let ok = 0;
        for (const [id, fields] of entries) {
          try { await onSave(rowId(id), fields); ok++; }
          catch { message.error(`خطا در ذخیره ردیف ${id}`); }
        }
        message.success(`${ok} ردیف ذخیره شد`);
        setChanges({}); setErrors({}); setEditMode(false); resetOwnSelection();
      }
    } finally { setSaving(false); }
  };

  const handleCancel = () => {
    if (changedCount > 0) {
      Modal.confirm({
        title: "تغییراتِ ذخیره‌نشده",
        content: `${changedCount} ردیف تغییر کرده. از ویرایش خارج شوید؟`,
        okText: "بله، خارج شو", cancelText: "ادامهٔ ویرایش",
        onOk: () => { setChanges({}); setErrors({}); setEditMode(false); resetOwnSelection(); },
      });
    } else { setChanges({}); setErrors({}); setEditMode(false); resetOwnSelection(); }
  };

  const applicableCols = useMemo(
    () => (columns || []).filter((c) => c.editType && c.dataIndex),
    [columns],
  );

  const applySampleRow = useMemo(() => {
    if (!selectedKeys.length || !dataSource) return null;
    return dataSource.find((r) => String(r[rowKey]) === String(selectedKeys[0])) || null;
  }, [selectedKeys, dataSource, rowKey]);

  const doApplyToSelected = () => {
    if (!applyField) { message.warning("ستون را انتخاب کنید"); return; }
    if (!selectedKeys.length) { message.warning("حداقل یک ردیف را انتخاب کنید"); return; }
    if (!applyConfirm) {
      setApplyConfirm(true);
      return;
    }
    const col = applicableCols.find((c) => c.dataIndex === applyField);
    const byId = new Map((dataSource || []).map((r) => [String(r[rowKey]), r]));
    selectedKeys.forEach((k) => {
      const row = byId.get(String(k));
      const st = row ? rowEditState(row) : { ok: false };
      if (st.ok) change(k, applyField, applyValue, col, row);
    });
    setApplyModal(false);
    setApplyConfirm(false);
    message.success(`مقدار روی ${selectedKeys.length} ردیفِ انتخاب‌شده اعمال شد (هنوز ذخیره نشده)`);
  };

  const renderEditor = (col, current, row, rowId) => {
    const err = errors[rowId]?.[col.dataIndex];
    const wrap = (node) => err
      ? <Tooltip title={err} open color="#cf1322">{node}</Tooltip>
      : node;
    const status = err ? "error" : undefined;
    if (col.editType === "number") return wrap(
      <InputNumber value={current} size="small" style={{ width: "100%" }} status={status}
        formatter={(v) => v?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
        parser={(v) => {
          if (v == null) return v;
          const t = String(v).replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d))
            .replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d))
            .replace(/,/g, "");
          const n = Number(t);
          return Number.isFinite(n) ? n : undefined;
        }}
        onChange={(v) => change(rowId, col.dataIndex, v, col, row)} />,
    );
    if (col.editType === "select") {
      const opts = optionsWithCurrent(resolveOptions(col, row), current, (v) => String(v));
      if (col.editAllowNew) {
        return wrap(
          <CreatableSelect value={current} options={opts} size="small"
            style={{ width: "100%" }} status={status} onCreate={col.editOnCreate}
            onChange={(v) => change(rowId, col.dataIndex, v, col, row)} />,
        );
      }
      return wrap(
        <Select
          value={current}
          size="small"
          style={{ width: "100%" }}
          status={status}
          allowClear={col.editAllowClear !== false}
          showSearch
          optionFilterProp="label"
          popupMatchSelectWidth={false}
          listHeight={opts.length > 12 ? 256 : undefined}
          options={opts}
          onChange={(v) => change(rowId, col.dataIndex, v ?? null, col, row)}
        />,
      );
    }
    if (col.editType === "boolean") return (
      <Switch size="small" checked={!!current}
        checkedChildren={col.onLabel || "دارد"} unCheckedChildren={col.offLabel || "ندارد"}
        onChange={(v) => change(rowId, col.dataIndex, v, col, row)} />
    );
    if (col.editType === "date") return wrap(
      <JalaliDateInput value={current} size="small"
        onChange={(v) => change(rowId, col.dataIndex, v, col, row)} />,
    );
    if (col.editType === "textarea") return wrap(
      <Input.TextArea value={current} size="small" autoSize={{ minRows: 1, maxRows: 4 }} status={status}
        onChange={(e) => change(rowId, col.dataIndex, e.target.value, col, row)} />,
    );
    return wrap(
      <Input value={current} size="small" status={status}
        onChange={(e) => change(rowId, col.dataIndex, e.target.value, col, row)} />,
    );
  };

  const builtColumns = useMemo(() => {
    const cols = (columns || []).map((col) => {
      if (!col.editType || !editMode) return col;
      return {
        ...col,
        render: (val, row) => {
          const rowId = row[rowKey];
          const st = rowEditState(row);
          if (!st.ok) {
            const orig = typeof col.render === "function" ? col.render(val, row) : (val ?? "—");
            return <Tooltip title={st.reason}><span style={{ opacity: 0.55 }}>{orig}</span></Tooltip>;
          }
          const current = changes[rowId]?.[col.dataIndex] !== undefined
            ? changes[rowId][col.dataIndex] : val;
          return renderEditor(col, current, row, rowId);
        },
      };
    });
    if (onDelete && !editMode) {
      cols.push({
        title: "", width: 60, key: "__del", gxFilter: false,
        render: (_, row) => (
          <Popconfirm title="حذف شود؟" onConfirm={() => onDelete(row[rowKey])} okText="بله" cancelText="خیر">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        ),
      });
    }
    return cols;
  }, [columns, editMode, changes, errors, rowKey, onDelete, rowEditState]);

  const rowErrorIds = Object.keys(errors).filter((id) => errors[id].__row);
  const applyCol = applicableCols.find((c) => c.dataIndex === applyField);
  const applyFieldTitle = applyCol
    ? (typeof applyCol.title === "string" ? applyCol.title : applyField)
    : applyField;

  const toolbar = (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
      <span style={{ fontWeight: 600, color: "var(--brand, #1F4E79)" }}>{title || ""}</span>
      <Space wrap>
        {extraToolbar}
        {!editMode ? (
          <Button icon={<EditOutlined />}
            onClick={() => startTransition(() => setEditMode(true))}
            style={{ borderColor: "#1F4E79", color: "#1F4E79" }}>
            ویرایش دسته‌جمعی
          </Button>
        ) : (
          <>
            {changedCount > 0 && <Tag color="orange">{changedCount} ردیف تغییر کرده</Tag>}
            {cellErrorCount > 0 && <Tag color="red">{cellErrorCount} خطا</Tag>}
            <Button icon={<ThunderboltOutlined />} disabled={!selectedKeys.length}
              onClick={() => {
                setApplyField(applicableCols[0]?.dataIndex || null);
                setApplyValue(null);
                setApplyConfirm(false);
                setApplyModal(true);
              }}>
              اعمال روی انتخاب‌شده‌ها{selectedKeys.length ? ` (${selectedKeys.length})` : ""}
            </Button>
            {/* حذفِ دسته‌جمعی — فقط اگر صفحه `onDeleteMany` داده باشد */}
            {onDeleteMany && (
              <Popconfirm
                title={`حذفِ ${selectedKeys.length} ردیفِ انتخاب‌شده`}
                description="این کار برگشت‌پذیر نیست."
                okText="حذف" cancelText="انصراف" okButtonProps={{ danger: true }}
                disabled={!selectedKeys.length}
                onConfirm={async () => {
                  setSaving(true);
                  try {
                    await onDeleteMany(selectedKeys);
                    setSelectedKeys([]);
                  } catch (e) {
                    const d = e?.response?.data?.detail;
                    message.error(typeof d === "string" ? d : d?.message || "حذف ناموفق بود");
                  }
                  setSaving(false);
                }}>
                <Button danger icon={<DeleteOutlined />} disabled={!selectedKeys.length}>
                  حذف انتخاب‌شده‌ها{selectedKeys.length ? ` (${selectedKeys.length})` : ""}
                </Button>
              </Popconfirm>
            )}
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSaveAll}
              style={{ background: "#107C41", borderColor: "#107C41" }}>
              ذخیره همه
            </Button>
            <Button icon={<CloseOutlined />} onClick={handleCancel}>انصراف</Button>
          </>
        )}
      </Space>
    </div>
  );

  const renderApplyValueEditor = () => {
    if (!applyCol) return <Input disabled />;
    if (applyCol.editType === "number") {
      return (
        <InputNumber value={applyValue} style={{ width: "100%" }}
          formatter={(v) => v?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
          onChange={(v) => { setApplyValue(v); setApplyConfirm(false); }} />
      );
    }
    if (applyCol.editType === "select") {
      const opts = optionsWithCurrent(resolveOptions(applyCol, applySampleRow), applyValue, (v) => String(v));
      if (applyCol.editAllowNew) {
        return (
          <CreatableSelect value={applyValue} options={opts} style={{ width: "100%" }}
            onCreate={applyCol.editOnCreate}
            onChange={(v) => { setApplyValue(v); setApplyConfirm(false); }} />
        );
      }
      return (
        <Select value={applyValue} style={{ width: "100%" }} allowClear showSearch
          optionFilterProp="label" options={opts}
          onChange={(v) => { setApplyValue(v); setApplyConfirm(false); }} />
      );
    }
    if (applyCol.editType === "boolean") {
      return (
        <Switch checked={!!applyValue} onChange={(v) => { setApplyValue(v); setApplyConfirm(false); }}
          checkedChildren={applyCol.onLabel || "دارد"} unCheckedChildren={applyCol.offLabel || "ندارد"} />
      );
    }
    if (applyCol.editType === "date") {
      return <JalaliDateInput value={applyValue} onChange={(v) => { setApplyValue(v); setApplyConfirm(false); }} />;
    }
    if (applyCol.editType === "textarea") {
      return (
        <Input.TextArea rows={3} value={applyValue}
          onChange={(e) => { setApplyValue(e.target.value); setApplyConfirm(false); }} />
      );
    }
    return (
      <Input value={applyValue}
        onChange={(e) => { setApplyValue(e.target.value); setApplyConfirm(false); }} />
    );
  };

  return (
    // فلش‌ها/Tab بینِ سلول‌های در حالِ ویرایش حرکت می‌کنند (Tab پایین می‌رود)
    <div onKeyDown={gridKeyNav}>
      {toolbar}
      {rowErrorIds.length > 0 && (
        <Alert type="error" showIcon style={{ marginBottom: 8 }}
          message="ذخیره انجام نشد — خطاهای ردیفی:"
          description={rowErrorIds.map((id) => <div key={id}>ردیف {id}: {errors[id].__row}</div>)} />
      )}
      <ErrorBoundary>
        <GxTable
          layoutId={layoutId}
          gxDefaultHidden={gxDefaultHidden}
          gxLayoutVersion={gxLayoutVersion}
          dataSource={dataSource}
          columns={builtColumns}
          rowKey={rowKey}
          loading={loading}
          size="small"
          scroll={scroll || { x: "max-content" }}
          pagination={pagination || { pageSize: 25 }}
          gxSummary={gxSummary}
          rowSelection={(editMode || selectionControlled) ? {
            selectedRowKeys: selectedKeys,
            onChange: setSelectedKeys,
            preserveSelectedRowKeys: true,
            getCheckboxProps: (row) => ({ disabled: editMode && !rowEditState(row).ok }),
          } : undefined}
          rowClassName={(row, i) => {
            const id = row[rowKey];
            if (errors[id]?.__row) return "gx-row-error";
            if (editMode && effectiveChanges[id]) return "ant-table-row-selected";
            return typeof rowClassNameProp === "function" ? rowClassNameProp(row, i) : "";
          }}
        />
      </ErrorBoundary>

      <Modal open={applyModal}
        title="اعمالِ یک مقدار روی ردیف‌های انتخاب‌شده"
        onOk={doApplyToSelected}
        onCancel={() => { setApplyModal(false); setApplyConfirm(false); }}
        okText={applyConfirm ? "تأیید و اعمال" : "ادامه"}
        cancelText="انصراف">
        <div style={{ display: "flex", flexDirection: "column", gap: 12, direction: "rtl" }}>
          {!applyConfirm ? (
            <>
              <div>
                <div style={{ marginBottom: 4 }}>ستون:</div>
                <Select value={applyField} style={{ width: "100%" }}
                  onChange={(v) => { setApplyField(v); setApplyValue(null); setApplyConfirm(false); }}
                  options={applicableCols.map((c) => ({
                    value: c.dataIndex,
                    label: typeof c.title === "string" ? c.title : c.dataIndex,
                  }))} />
              </div>
              <div>
                <div style={{ marginBottom: 4 }}>مقدار جدید:</div>
                {renderApplyValueEditor()}
              </div>
              <Alert type="info" showIcon
                message={`${selectedKeys.length} ردیف انتخاب شده — پس از ادامه، خلاصه را تأیید کنید.`} />
            </>
          ) : (
            <Alert type="warning" showIcon
              message="خلاصهٔ اعمال"
              description={
                <div style={{ lineHeight: 2 }}>
                  <div><b>{selectedKeys.length}</b> ردیف انتخاب شده</div>
                  <div>فیلد: <b>{applyFieldTitle}</b></div>
                  <div>مقدار جدید: <b>{optionLabel(applyCol, applyValue, applySampleRow)}</b></div>
                  <div style={{ opacity: 0.75, fontSize: 12 }}>تا «ذخیره همه» چیزی در سرور ثبت نمی‌شود.</div>
                </div>
              } />
          )}
        </div>
      </Modal>
    </div>
  );
}
